import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import CheckmarkSquareIcon from '../../../../assets/icons/CheckmarkSquareIcon';
import { APIPickTour } from '../../../../redux/sagas/types';
import {
  mergeClassNames,
  displayOrderType,
  determineIconByOrderType,
  displayDestination,
  displayDate,
  epRegEx,
  getFormattedDestination,
} from '../../../../utils';

import styles from '../PickTourItem/PickTourItem.css';

type Props = {
  pickTour: APIPickTour;
  isSelected: boolean;
  onSelect: () => void;
};

const PickTourWithErrorItem = ({ pickTour, isSelected, onSelect }: Props): ReactElement => {
  const { t } = useTranslation();
  const translationRoot = 'ORDER_LIST.LIST_ITEM';
  const { pickTourID, metadata, pickTourList, createTS, pickTourType } = pickTour;
  const pickTourHasNoItems = pickTourList.length === 0;

  const getFirstDestination = (): string | null => {
    if (metadata.ordersSummaryData.length > 0 && metadata.ordersSummaryData[0].destination) {
      return metadata.ordersSummaryData[0].destination;
    }
    return null;
  };

  const pickTourDestination = getFirstDestination();

  return (
    <li
      className={mergeClassNames(styles.li, styles.errorItem)}
      onClick={() => onSelect()}
      data-cy={`pickTour-list-item-${pickTourID}`}
    >
      <div className={styles.pickInfo}>
        <div className={styles.checkboxArea}>
          {isSelected ? <CheckmarkSquareIcon /> : <div className={styles.circle} data-cy="circle-icon" />}
        </div>
        <div className={styles.mainArea}>
          <div className={styles.destination} data-cy="pickTour-destination">
            {getFormattedDestination(pickTourDestination)}
          </div>
          <div className={styles.content}>
            <div className={styles.orderType}>
              <div className={styles.icon}>
                {determineIconByOrderType({
                  orderType: pickTourHasNoItems ? pickTourType : pickTourList[0].orderType,
                })}
              </div>
              <p data-cy="pickTour-type">
                {displayOrderType(
                  pickTourHasNoItems ? pickTourType : pickTourList[0].orderType,
                  pickTourDestination?.match(epRegEx) !== null ? 'Commercial - Delivery' : null,
                  pickTourDestination
                )}
              </p>
            </div>
            <div className={styles.itemsPick}>
              <span>{t(`${translationRoot}.ITEMS_TO_PICK`)}</span>
              <span className={styles.quantity}>{metadata.unhandledItemsRemaining}</span>
            </div>
            <div>
              <span className={styles.textLight}>{displayDate({ date: createTS }).split('-')[0]}</span>
              <span className={styles.pickTourTime}>{displayDate({ date: createTS }).split('-')[1]}</span>
            </div>
            <div className={styles.highPriority}>
              <span className={styles.textFailed}>{t(`${translationRoot}.ORDER_FINALIZATION_FAILED`)}</span>
            </div>
          </div>
        </div>
      </div>

      {isSelected && (
        <div className={styles.pickOrdersWrapper}>
          {metadata.ordersSummaryData.map(orderSummary => (
            <div key={orderSummary.orderID} className={styles.pickOrders}>
              <span className={styles.pickOrderDestination}>
                {displayDestination({
                  orderType: pickTourType,
                  destination: orderSummary.destination,
                })}
              </span>
              <span className={styles.textLight}>{t(`${translationRoot}.QTY`)}</span>
              <span className={styles.textLight}>{orderSummary.qty}</span>
            </div>
          ))}
        </div>
      )}
    </li>
  );
};

export default PickTourWithErrorItem;
